import { ComboboxItem, MultiSelect, MultiSelectProps } from '@mantine/core'
import { useInfiniteQuery, UseQueryOptions } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useDebouncedValue } from '@mantine/hooks'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'

export const Input_MultiSelect = ({
  queryKey,
  queryFn,
  nothingFound,
  icon,
  disabled,
  ...props
}: Omit<MultiSelectProps, 'data' | 'nothingFound'> & {
  nothingFound?: React.ReactNode
  icon?: React.ReactNode | IconType
} & Pick<UseQueryOptions<ComboboxItem[]>, 'queryKey'> & {
    queryFn: (params: Record<string, string>) => Promise<ComboboxItem[]>
    name: string
  }) => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 500)
  const leftSection =
    typeof icon === 'function'
      ? (() => {
          const Icon = icon
          return <Icon size={'1.2em'} />
        })()
      : icon
  const { data, isLoading, fetchNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => queryFn({ search: pageParam }),
    enabled: !disabled,
  })

  useEffect(() => {
    if (!debouncedSearch || disabled) return

    fetchNextPage({ pageParam: debouncedSearch })
  }, [debouncedSearch, disabled, fetchNextPage])

  const items = useMemo(
    () =>
      data?.pages
        ? [
            ...new Map(
              data.pages
                .flatMap(page => page)
                .map(item => [item['value'], item]),
            ).values(),
          ]
        : [],
    [data],
  )

  return (
    <Controller
      control={control}
      name={props.name}
      render={({ field, fieldState }) => {
        const { error } = fieldState
        return (
          <MultiSelect
            disabled={disabled || isLoading}
            data={items}
            searchable
            onSearchChange={setSearch}
            error={error?.message}
            clearable
            nothingFoundMessage={nothingFound ?? t('generic.noData')}
            leftSection={leftSection}
            {...props}
            {...field}
          />
        )
      }}
    />
  )
}
